import { useState, useEffect, useCallback } from 'react'
import { chatterService, ChatterMessage as ChatterMessageType, SendMessageData } from '../services/chatterService'

interface UseChatterOptions {
  opportunityId: string
  autoMarkAsRead?: boolean
  enableRealTime?: boolean
}

interface UseChatterReturn {
  messages: ChatterMessageType[]
  businessName?: string
  loading: boolean
  error: string | null
  sendMessage: (messageData: SendMessageData) => Promise<void>
  markAsRead: () => Promise<void>
  refreshMessages: () => Promise<void>
  sending: boolean
}

export const useChatter = ({
  opportunityId,
  autoMarkAsRead = true,
  enableRealTime = true
}: UseChatterOptions): UseChatterReturn => {
  const [messages, setMessages] = useState<ChatterMessageType[]>([])
  const [businessName, setBusinessName] = useState<string>()
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load messages
  const loadMessages = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await chatterService.getOpportunityMessages(opportunityId)
      setMessages(data.messages)
      setBusinessName(data.businessName)
      
      // Auto mark as read when messages are loaded
      if (autoMarkAsRead && data.messages.some(msg => !msg.isRead)) {
        await chatterService.markAsRead(opportunityId)
        // Update local state to reflect read status
        setMessages(prev => prev.map(msg => ({ ...msg, isRead: true })))
        // Notify other parts of the app that messages were read
        window.dispatchEvent(new CustomEvent('chatter:message-read', { 
          detail: { opportunityId } 
        }))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages')
      console.error('Error loading chatter messages:', err)
    } finally {
      setLoading(false)
    }
  }, [opportunityId, autoMarkAsRead])

  // Send message
  const sendMessage = useCallback(async (messageData: SendMessageData) => {
    try {
      setSending(true)
      setError(null)
      
      const newMessage = await chatterService.sendMessage(opportunityId, messageData)
      
      // Add message to local state immediately
      setMessages(prev => [...prev, newMessage])
      
      // Notify other parts of the app that ISO sent a message (no red dot needed)
      window.dispatchEvent(new CustomEvent('chatter:iso-message-sent', { 
        detail: { opportunityId, messageData: newMessage } 
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
      console.error('Error sending message:', err)
      throw err // Re-throw so component can handle it
    } finally {
      setSending(false)
    }
  }, [opportunityId])

  // Mark as read
  const markAsRead = useCallback(async () => {
    try {
      await chatterService.markAsRead(opportunityId)
      setMessages(prev => prev.map(msg => ({ ...msg, isRead: true })))
      // Notify other parts of the app that messages were read
      window.dispatchEvent(new CustomEvent('chatter:message-read', { 
        detail: { opportunityId } 
      }))
    } catch (err) {
      console.error('Error marking as read:', err)
    }
  }, [opportunityId])

  // Refresh messages
  const refreshMessages = useCallback(async () => {
    await loadMessages()
  }, [loadMessages])

  // Load messages on mount and when opportunityId changes
  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  // Set up real-time updates via Server-Sent Events
  useEffect(() => {
    if (!enableRealTime) return

    let eventSource: EventSource | null = null

    try {
      eventSource = chatterService.createSSEConnection(opportunityId)

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          if (data.type === 'new_message') {
            // Refresh messages when new message arrives
            loadMessages()
            // Notify other parts of the app about the new message
            window.dispatchEvent(new CustomEvent('chatter:new-message', { 
              detail: { opportunityId, messageData: data } 
            }))
          } else if (data.type === 'connected') {
            console.log('🔗 Connected to chatter real-time updates')
          } else if (data.type === 'heartbeat') {
            // Keep connection alive
          }
        } catch (err) {
          console.error('Error parsing SSE message:', err)
        }
      }

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)
        // Connection will automatically retry
      }

      eventSource.onopen = () => {
        console.log('📡 SSE connection opened for opportunity:', opportunityId)
      }

    } catch (err) {
      console.error('Error setting up SSE connection:', err)
    }

    // Cleanup on unmount or opportunityId change
    return () => {
      if (eventSource) {
        eventSource.close()
        console.log('📡 SSE connection closed for opportunity:', opportunityId)
      }
    }
  }, [opportunityId, enableRealTime, loadMessages])

  return {
    messages,
    businessName,
    loading,
    error,
    sendMessage,
    markAsRead,
    refreshMessages,
    sending
  }
}
